import { useState } from 'react'
import { X, User, Mail } from 'lucide-react'
import axios from 'axios'
import toast from 'react-hot-toast'

interface ProfileSetupModalProps {
  isOpen: boolean
  walletAddress: string
  onComplete: (userData: { username: string; email: string }) => void
}

export default function ProfileSetupModal({ isOpen, walletAddress, onComplete }: ProfileSetupModalProps) {
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!username.trim()) {
      toast.error('Username is required')
      return
    }

    setLoading(true)
    
    try {
      await axios.post('/api/users/profile', {
        walletAddress,
        username: username.trim(),
        email: email.trim() || null
      })
      
      toast.success('Profile created!')
      onComplete({ username: username.trim(), email: email.trim() })
    } catch (error: any) {
      console.error('Profile creation failed:', error)
      toast.error(error.response?.data?.error || 'Failed to create profile')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="relative bg-[#001233] rounded-xl max-w-md w-full p-8 border border-[#2AAAEA]/30 shadow-2xl shadow-[#2AAAEA]/20">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2AAAEA]/5 via-transparent to-[#FBB03B]/5 rounded-xl pointer-events-none"></div>
        
        <div className="relative">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#2AAAEA] via-[#052393] to-[#FBB03B] rounded-full flex items-center justify-center mb-2">
              <User className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h2 className="text-3xl font-black text-white text-center mb-2">Welcome to CPX!</h2>
          <p className="text-gray-300 text-center mb-8">
            Complete your profile to start trading
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                <User className="w-4 h-4 inline mr-2 text-cpx-light-blue" />
                Username *
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-[#001a3d] border border-[#052393]/40 rounded-lg text-white placeholder-gray-500 focus:border-[#2AAAEA] focus:ring-2 focus:ring-[#2AAAEA]/30 transition-all"
                placeholder="Your display name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                <Mail className="w-4 h-4 inline mr-2 text-cpx-light-blue" />
                Email (optional)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#001a3d] border border-[#052393]/40 rounded-lg text-white placeholder-gray-500 focus:border-[#2AAAEA] focus:ring-2 focus:ring-[#2AAAEA]/30 transition-all"
                placeholder="your@email.com"
              />
              <p className="text-xs text-gray-500 mt-2">
                We won't validate or spam - just for display
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Profile...' : 'Complete Setup'}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-[#052393]/30">
            <p className="text-xs text-gray-500 text-center">
              Connected: {walletAddress.slice(0, 6)}...{walletAddress.slice(-6)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

